import { Component, computed, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterInput } from '@it2go/data-grid';
import { PersonGridItemObject } from '@it2go/types';
import {
    FolderPersonHistoryObject,
} from '@libs/types/src/services/object-type/folder/folder-person-history.object';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { FolderPersonsStore } from '../folder-persons.store';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { CardTableComponent } from '../../../../../modules/shared/components/kit/table/card-table.component';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { TableColumn } from '../../../../../modules/shared/components/kit/table/table.types';
import {
    dateFromColumn,
    dateToColumn,
    folderPersonColumn,
} from '../../../../../modules/shared/components/kit/table/table.fields';
import { folderPersonHistoryRowFrag } from '../../../../../services/types/folder.types';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { CancelButtonComponent } from '../../../../kit/button/cancel-button.component';
import { SaveButtonComponent } from '../../../../kit/button/save-button.component';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { FormatPersonPipe } from '../../../../../modules/shared/pipes/format-person.pipe';
import { fb } from '../../../../../modules/shared/shared.global-service';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { dateFromBeforeDateTo } from '../../../../../utils/date.form-validator';
import { InfiniteScrollStore } from '../../../../../modules/shared/store/common/infinite-scroll.store';
import { folderPersonSearchFrag } from '../folder-person-dialog/folder-person-dialog.query';
import { dateify } from '../../../../../modules/shared/helpers/date.helper';
import { ConfirmBuilder } from '../../../../../modules/shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import { PrimeTemplate } from '@openng/optimus-ui/api';

/**
 * Historie pobytů ve složce - i pobyty osob, které už ve složce nejsou, a zemřelých.
 * Bez `personId` jde o celou složku, s `personId` jen o pobyty jedné osoby.
 * Data dialogu: { folderId: number, personId?: number }
 *
 * Pobyt se edituje přímo tady - klik na řádek naplní formulář nad tabulkou, včetně probíhajícího
 * pobytu. Členství ve složce dialog nikdy nemění: ukončení pobytu jen říká, do kdy byla osoba na
 * službě, ve složce zůstane (i s primárností a vlastnictvím) až do akce "Odebrat ze složky".
 *
 * Formulář bez vybraného řádku zakládá nový pobyt - typicky doplnění období, které se nikdy
 * nezapsalo. Do složky tím osoba nepřibude, na to je akce "Přidat osobu do složky".
 * V režimu celé složky se k novému pobytu vybírá i osoba.
 */
@Component({
    selector: 'app-folder-persons-history-dialog',
    templateUrl: 'folder-persons-history-dialog.component.html',
    providers: [FolderPersonsStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DatePipe,
        TranslatePipe,
        CardTableComponent,
        ButtonComponent,
        CancelButtonComponent,
        SaveButtonComponent,
        DateInputComponent,
        SelectInputComponent,
        TextInputComponent,
        FormatPersonPipe,
        PrimeTemplate,
    ],
})
export class FolderPersonsHistoryDialogComponent extends BaseDialogComponent {

    public readonly store = inject(FolderPersonsStore);
    protected readonly folderStore = inject(FolderGlobalStore);
    private readonly translate = inject(TranslateService);
    private readonly confirm = inject(ConfirmBuilder);

    protected readonly prefix = 'folder.persons.';
    protected readonly folderPersonHistoryRowFrag = folderPersonHistoryRowFrag;

    protected readonly folderId = Number(this.dialogConfig.data?.folderId);
    protected readonly personId = this.dialogConfig.data?.personId
        ? Number(this.dialogConfig.data.personId)
        : null;

    protected readonly table = viewChild<TableComponent>('table');

    /** U jedné osoby je sloupec se jménem zbytečný - je v hlavičce dialogu. */
    protected columns: TableColumn[] = [
        ...(this.personId ? [] : [folderPersonColumn]),
        dateFromColumn,
        dateToColumn,
        { name: 'note' },
        { name: 'actions' },
    ];

    protected readonly tableTitle = this.prefix + (this.personId ? 'stayHistory' : 'history');

    /** Pobyt vybraný klikem v tabulce - jeho datumy a poznámku drží formulář. Bez něj se zakládá nový. */
    protected readonly selected = signal<FolderPersonHistoryObject | null>(null);

    protected form = new AppFormGroup({
        personId: fb.control<number | null>(null),
        dateFrom: fb.control<Date | null>(null, Validators.required),
        dateTo: fb.control<Date | null>(null),
        note: fb.control<string | null>(null),
    });

    /**
     * Osoby celé organizace - pobyt se doplňuje i lidem, kteří ve složce dávno nejsou nebo v ní
     * nikdy nebyli, nabídku tedy nejde omezit na osoby složky. Zemřelí se nabízejí taky, minulý
     * pobyt jim vzniknout může. Shodnou organizaci si hlídá i server.
     */
    protected readonly personScroll = computed(() => {
        if (this.personId) return null;

        const organizationId = this.store.globalGlobalStore.organizationStore.selectedId();
        if (!organizationId) return null;

        return new InfiniteScrollStore<PersonGridItemObject>(
            this.store.api.getPersons,
            new FilterBuilder().where('organizationId', String(organizationId)).limit(20).filter(),
            true,
            folderPersonSearchFrag,
        );
    });

    private readonly canEditFolder = this.store.isAllowed('folder.update');

    /** Editovat lze jen v neukončené složce a s právem na úpravu. */
    protected readonly canEdit = computed(() => this.canEditFolder() && !this.folderStore.isTerminated());

    protected additionalFilters = computed<FilterInput[][]>(() => {
        return FilterBuilder.singlePage({
            folderId: this.folderId,
            ...(this.personId ? { personId: this.personId } : {}),
        }).filter || [];
    });

    /** Zemřelí i ukončené pobyty zůstávají v historii, jen se vizuálně odliší. */
    protected rowClass = (item: FolderPersonHistoryObject) => ({
        ...item,
        trClass: item.isDeceased ? 'table-row-deceased' : (item.dateTo ? 'table-row-inactive' : ''),
    });

    constructor() {
        super();

        // datum do smí chybět (probíhající pobyt), ale nikdy nesmí předcházet datu od
        this.form.controls.dateTo.setValidators(dateFromBeforeDateTo(this.form));
        this.subs.push(this.form.controls.dateFrom.valueChanges.subscribe(
            () => this.form.controls.dateTo.updateValueAndValidity(),
        ));

        this.syncPersonValidator();
    }

    /** Položka je kopie z `rowClass`, ale `id`, datumy i poznámka sedí. */
    protected onRowSelected(item: FolderPersonHistoryObject | null): void {
        this.selected.set(item ?? null);
        this.form.reset();
        this.syncPersonValidator();

        if (!item) return;

        this.form.patchValue({
            personId: item.personId,
            dateFrom: dateify(item.dateFrom),
            dateTo: item.dateTo ? dateify(item.dateTo) : null,
            note: item.note ?? null,
        });
    }

    /** Pustí formulář z editace vybraného řádku do zakládání nového pobytu. */
    protected newStay(): void {
        this.table()?.clearSelection();
        this.onRowSelected(null);
    }

    /** Schválně ne `save()` z BaseDialogComponent - uložení pobytu dialog nezavírá. */
    protected async saveStay(): Promise<void> {
        if (this.form.isInvalid()) return;

        const stay = this.selected();

        return stay ? this.updateStay(stay) : this.createStay();
    }

    private async createStay(): Promise<void> {
        // překryv pobytů, pořadí datumů i cizí organizaci hlídá server,
        // chybu vypíše globální handler
        await this.store.api.createFolderPersonHistory({
            folderId: this.folderId,
            personId: this.personId ?? this.form.value.personId!,
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo?.toISOString() || null,
            note: this.form.value.note || null,
        });

        contentGlobalSuccess.next(`${this.prefix}messages.stayCreated`);
        await this.reload();
    }

    private async updateStay(stay: FolderPersonHistoryObject): Promise<void> {
        // překryv pobytů i pořadí datumů hlídá server, chybu vypíše globální handler
        await this.store.api.updateFolderPersonHistory({
            id: stay.id,
            dateFrom: this.form.value.dateFrom!.toISOString(),
            dateTo: this.form.value.dateTo?.toISOString() || null,
            note: this.form.value.note || null,
        });

        contentGlobalSuccess.next(`${this.prefix}messages.updated`);
        await this.reload();
    }

    protected deleteStay(stay: FolderPersonHistoryObject): void {
        this.confirm.confirm(
            `${this.prefix}actions.deleteStay`,
            `${this.prefix}messages.confirmDeleteStay`,
            async () => {
                await this.store.api.deleteFolderPersonHistory({ id: stay.id });
                contentGlobalSuccess.next(`${this.prefix}messages.stayDeleted`);
                await this.reload();
            },
        );
    }

    protected deleteTooltip(stay: FolderPersonHistoryObject): string {
        return this.translate.instant(`${this.prefix}actions.${stay.isActive ? 'deleteStayActive' : 'deleteStay'}`);
    }

    /** Osobu vybírá uživatel jen u nového pobytu v režimu celé složky - u editace ji drží řádek. */
    private syncPersonValidator(): void {
        if (this.personId) return;

        const control = this.form.controls.personId;
        control.setValidators(this.selected() ? [] : [Validators.required]);
        control.updateValueAndValidity({ emitEvent: false });
    }

    private async reload(): Promise<void> {
        this.onRowSelected(null);
        this.table()?.clearSelection();
        await this.table()?.reload();
    }

}
